import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MapPin, Play, Square, Plus, Trash2, Target, ChevronDown, ChevronUp, Footprints, Sparkles, X, Check, Home } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Haversine distance in meters between two lat/lon points
function haversine(a, b) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function WalkJourney({ onUpdate }) {
  const [today, setToday] = useState(null);
  const [missions, setMissions] = useState(null);
  const [doghouse, setDoghouse] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [manualKm, setManualKm] = useState('');
  const [goalInput, setGoalInput] = useState('');
  const [showGoalEditor, setShowGoalEditor] = useState(false);

  // GPS state
  const [tracking, setTracking] = useState(false);
  const [currentMeters, setCurrentMeters] = useState(0);
  const [gpsError, setGpsError] = useState(null);
  const watchIdRef = useRef(null);
  const lastPosRef = useRef(null);
  const startedAtRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [t, m, d] = await Promise.all([
        axios.get(`${API}/walk/today`, { withCredentials: true }),
        axios.get(`${API}/walk/missions`, { withCredentials: true }),
        axios.get(`${API}/walk/doghouse`, { withCredentials: true }),
      ]);
      setToday(t.data);
      setMissions(m.data);
      setDoghouse(d.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ----- GPS tracking -----
  const startTracking = async () => {
    if (!navigator.geolocation) {
      toast.error('GPS stöds inte av denna webbläsare — använd manuell input istället 🌸');
      return;
    }
    setGpsError(null);
    setCurrentMeters(0);
    lastPosRef.current = null;
    startedAtRef.current = new Date().toISOString();
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const cur = { lat: pos.coords.latitude, lon: pos.coords.longitude, acc: pos.coords.accuracy };
        if (lastPosRef.current && cur.acc && cur.acc < 40) {
          const d = haversine(lastPosRef.current, cur);
          // Ignore tiny jitter < 3 m or implausible jumps > 200 m per tick
          if (d >= 3 && d < 200) {
            setCurrentMeters((m) => m + Math.round(d));
          }
        }
        lastPosRef.current = cur;
      },
      (err) => {
        setGpsError(err.message || 'Kunde inte hämta GPS-position');
        setTracking(false);
        if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 },
    );
  };

  const stopTracking = async () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setTracking(false);
    const meters = currentMeters;
    if (meters < 50) {
      toast('Kortare än 50 m — sparar inte. Prova igen 🌸', { duration: 2500 });
      setCurrentMeters(0);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/walk/session`,
        { meters, source: 'gps', started_at: startedAtRef.current, ended_at: new Date().toISOString() },
        { withCredentials: true },
      );
      announce(data);
    } catch (e) {
      toast.error(`Kunde inte spara: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCurrentMeters(0);
      onUpdate?.();
      load();
    }
  };

  const submitManual = async (e) => {
    e?.preventDefault?.();
    const km = parseFloat((manualKm || '').replace(',', '.'));
    if (!Number.isFinite(km) || km <= 0) {
      toast.error('Skriv ett positivt tal (t.ex. 2,5)');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/walk/manual`, { km }, { withCredentials: true });
      announce(data);
      setManualKm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      onUpdate?.();
      load();
    }
  };

  const removeSession = async (id) => {
    try {
      await axios.delete(`${API}/walk/session/${id}`, { withCredentials: true });
      load();
      onUpdate?.();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const saveGoal = async () => {
    const km = parseFloat((goalInput || '').replace(',', '.'));
    if (!Number.isFinite(km) || km < 0.5 || km > 50) {
      toast.error('Välj mellan 0,5 och 50 km');
      return;
    }
    try {
      await axios.patch(`${API}/walk/goal`, { goal_m: Math.round(km * 1000) }, { withCredentials: true });
      toast.success(`🎯 Nytt mål: ${km} km/dag`);
      setShowGoalEditor(false);
      load();
    } catch (e) { toast.error('Kunde inte spara mål'); }
  };

  const announce = (data) => {
    const km = data.session?.meters ? (data.session.meters / 1000).toFixed(2) : '0';
    let msg = `🐾 +${km} km · +${data.points || 0}p`;
    if (data.milestones_unlocked?.length) {
      const names = data.milestones_unlocked.map((x) => `${x.emoji} ${x.label}`).join(' · ');
      msg += ` · ${names}`;
    }
    if (data.goal_reached) msg += ' · 🎉 Dagens mål klart!';
    toast.success(msg, { duration: 4000 });
  };

  const completeMission = async (slotIdx) => {
    try {
      const { data } = await axios.post(`${API}/walk/missions/${slotIdx}/complete`, {}, { withCredentials: true });
      if (data.already_done) return;
      toast.success(`${data.mission.emoji} +${data.points}p — ${data.mission.text}`, { duration: 4000 });
      load();
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte klara uppdraget');
    }
  };

  const skipMission = async (slotIdx) => {
    try {
      await axios.post(`${API}/walk/missions/${slotIdx}/skip`, {}, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  if (!today) return null;

  const goalKm = (today.goal_m / 1000).toFixed(1);
  const kmToday = today.km_today.toFixed(2);
  const pct = today.goal_pct;
  const passedIds = new Set(today.milestones_passed.map((x) => x.m));

  return (
    <div className="neo-card p-3 bg-gradient-to-br from-[#E8F7FF] via-white to-[#FFF5E6] mb-4" data-testid="walk-journey-widget">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="walk-toggle"
      >
        <div className="w-10 h-10 rounded-full bg-[#B9FBC0] border-2 border-black flex items-center justify-center text-xl shrink-0">
          🚶
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold">
            Dagens promenad · {kmToday} / {goalKm} km
          </p>
          <CorgiPath pct={pct} />
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Control row */}
          <div className="grid sm:grid-cols-3 gap-2">
            {!tracking ? (
              <button
                onClick={startTracking}
                className="neo-btn py-2 px-3 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1.5"
                data-testid="walk-start-gps"
              >
                <Play className="w-4 h-4" /> Starta promenad (GPS)
              </button>
            ) : (
              <button
                onClick={stopTracking}
                className="neo-btn py-2 px-3 bg-[#FFC8DD] font-bold text-sm flex items-center justify-center gap-1.5 animate-pulse-soft"
                data-testid="walk-stop-gps"
              >
                <Square className="w-4 h-4" /> Stoppa · {(currentMeters / 1000).toFixed(2)} km
              </button>
            )}

            <form onSubmit={submitManual} className="flex items-center gap-1 neo-card px-2 bg-white">
              <input
                type="text"
                inputMode="decimal"
                value={manualKm}
                onChange={(e) => setManualKm(e.target.value)}
                placeholder="km (t.ex. 2,5)"
                className="flex-1 text-sm px-1 py-2 bg-transparent outline-none"
                data-testid="walk-manual-input"
              />
              <button type="submit" className="neo-btn px-2 py-1 bg-[#FFE8A3] text-xs font-bold" data-testid="walk-manual-submit">
                <Plus className="w-3 h-3 inline" /> Lägg till
              </button>
            </form>

            <button
              onClick={() => { setShowGoalEditor((v) => !v); setGoalInput(goalKm); }}
              className="neo-btn py-2 px-3 bg-white text-xs flex items-center justify-center gap-1"
              data-testid="walk-edit-goal"
            >
              <Target className="w-4 h-4" /> Mål {goalKm} km
            </button>
          </div>

          {gpsError && (
            <p className="text-[11px] text-red-600 bg-red-50 border-2 border-black rounded-xl p-2">
              GPS: {gpsError} — använd manuell input istället 🌸
            </p>
          )}

          {showGoalEditor && (
            <div className="flex items-center gap-2 neo-card p-2 bg-[#FFFEF5]">
              <span className="text-xs font-bold">Nytt mål (km):</span>
              <input
                type="text"
                inputMode="decimal"
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
                className="flex-1 px-2 py-1 text-sm neo-card bg-white"
                data-testid="walk-goal-input"
              />
              <button onClick={saveGoal} className="neo-btn px-2 py-1 bg-[#B9FBC0] text-xs font-bold" data-testid="walk-goal-save">Spara</button>
            </div>
          )}

          {/* Milestones */}
          <div className="flex items-center justify-between gap-1 text-[11px]">
            {today.milestones.map((m) => {
              const passed = passedIds.has(m.m);
              return (
                <div
                  key={m.m}
                  className={`flex-1 text-center ${passed ? '' : 'opacity-40 grayscale'}`}
                  title={`${m.m / 1000} km · ${m.label}`}
                  data-testid={`walk-milestone-${m.m}`}
                >
                  <div className="text-lg">{m.emoji}</div>
                  <div className="text-[10px] font-bold">{m.m / 1000} km</div>
                </div>
              );
            })}
          </div>

          {/* Sessions list */}
          {today.sessions.length > 0 && (
            <div className="space-y-1">
              <p className="text-[11px] font-bold opacity-70">Dagens passager</p>
              {today.sessions.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-xs neo-card p-1.5 bg-white" data-testid={`walk-session-${s.id}`}>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-black bg-[#FFFEF5]">
                    {s.source === 'gps' ? '📍 GPS' : '✍️ Manuell'}
                  </span>
                  <span className="font-bold">{(s.meters / 1000).toFixed(2)} km</span>
                  <span className="text-[10px] opacity-60 ml-auto">
                    {s.created_at ? new Date(s.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                  <button onClick={() => removeSession(s.id)} className="neo-btn p-1 bg-white" data-testid={`walk-remove-${s.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Corgi's pastel world */}
          <CorgiWorld pct={pct} milestones={today.milestones} passedIds={passedIds} />

          {/* Missions */}
          {missions?.slots?.length > 0 && (
            <MissionList missions={missions} onComplete={completeMission} onSkip={skipMission} />
          )}
          {missions && missions.slots_unlocked === 0 && (
            <p className="text-[11px] text-center opacity-60 py-2">
              🔒 Gå 500 m för att låsa upp ditt första uppdrag
            </p>
          )}

          {/* Dog house */}
          {doghouse && <DogHouse data={doghouse} />}
        </div>
      )}
    </div>
  );
}


function CorgiPath({ pct }) {
  // A pastel path with a corgi positioned along it based on pct (0-100)
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="relative h-7 mt-1 rounded-full bg-gradient-to-r from-[#FFE8F5] via-[#FFF5E6] to-[#E8F7FF] border-2 border-black overflow-hidden">
      {/* Pastel dashed path */}
      <div className="absolute inset-y-0 left-2 right-2 flex items-center">
        <div className="w-full border-t-2 border-dashed border-[#C8B6FF] opacity-70" />
      </div>
      {/* Milestone dots */}
      {[20, 40, 60, 80].map((x) => (
        <span
          key={x}
          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#FFC8DD] border border-black"
          style={{ left: `${x}%` }}
        />
      ))}
      {/* Corgi marker */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-lg transition-all duration-700 ease-out"
        style={{ left: `${clamped}%` }}
      >
        <span className="drop-shadow-[0_0_2px_rgba(0,0,0,0.4)]">🐕</span>
      </div>
      {/* Home marker at 100% */}
      <span className="absolute top-1/2 -translate-y-1/2 right-1 text-sm">🏡</span>
    </div>
  );
}

// ============= Phase 3 — Corgi world (SVG pastel scene) =============
function CorgiWorld({ pct, milestones, passedIds }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const hour = new Date().getHours();
  // Sky color by time of day
  const sky =
    hour < 6 || hour >= 20 ? { top: '#1f1d3a', bot: '#6b4e8f' }       // night
    : hour < 9 ? { top: '#FFD6A5', bot: '#FFE8F5' }                    // dawn
    : hour < 16 ? { top: '#CDEEFF', bot: '#FFFEF5' }                   // day
    : { top: '#FFC8DD', bot: '#FFE8A3' };                              // sunset
  return (
    <div className="neo-card p-0 overflow-hidden bg-white" data-testid="walk-corgi-world">
      <svg viewBox="0 0 400 140" className="w-full block" preserveAspectRatio="none" style={{ height: 140 }}>
        {/* Sky gradient */}
        <defs>
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={sky.top} />
            <stop offset="100%" stopColor={sky.bot} />
          </linearGradient>
          <linearGradient id="hill1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8E6C9" />
            <stop offset="100%" stopColor="#81C784" />
          </linearGradient>
          <linearGradient id="hill2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F8BBD0" />
            <stop offset="100%" stopColor="#F48FB1" />
          </linearGradient>
          <linearGradient id="hill3" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E1BEE7" />
            <stop offset="100%" stopColor="#CE93D8" />
          </linearGradient>
        </defs>
        <rect width="400" height="140" fill="url(#sky)" />
        {/* Sun / moon */}
        {(hour < 6 || hour >= 20)
          ? <circle cx="330" cy="28" r="10" fill="#fff6bd" />
          : <circle cx="330" cy="28" r="14" fill="#FFE08A" opacity="0.85" />}
        {/* Background hills */}
        <path d="M0 85 Q 80 55 160 80 T 320 75 T 400 85 L 400 140 L 0 140 Z" fill="url(#hill3)" opacity="0.6" />
        <path d="M0 95 Q 60 70 140 92 T 300 90 T 400 100 L 400 140 L 0 140 Z" fill="url(#hill2)" opacity="0.7" />
        <path d="M0 108 Q 80 90 180 108 T 400 112 L 400 140 L 0 140 Z" fill="url(#hill1)" />
        {/* Path line */}
        <path d="M 10 118 Q 100 112 200 120 T 390 118" stroke="#C8B6FF" strokeWidth="3" strokeDasharray="4 4" fill="none" opacity="0.8" />
        {/* Milestone landmarks */}
        {milestones?.map((m, i) => {
          const x = 40 + i * 80;
          const passed = passedIds.has(m.m);
          return (
            <g key={m.m} opacity={passed ? 1 : 0.5} style={{ filter: passed ? '' : 'grayscale(1)' }}>
              <text x={x} y="108" fontSize="18" textAnchor="middle">{m.emoji}</text>
              <text x={x} y="134" fontSize="8" textAnchor="middle" fill="#555" fontWeight="bold">{m.m / 1000} km</text>
            </g>
          );
        })}
        {/* Tiny clouds */}
        <ellipse cx="60" cy="30" rx="14" ry="5" fill="#fff" opacity="0.85" />
        <ellipse cx="200" cy="18" rx="18" ry="6" fill="#fff" opacity="0.75" />
        {/* Corgi — moves across the scene based on pct */}
        <g style={{ transform: `translateX(${clamped * 3.8}px)`, transition: 'transform 0.8s ease-out' }}>
          <text x="10" y="122" fontSize="22" className="corgi-bob">🐕</text>
        </g>
      </svg>
      <style>{`
        .corgi-bob { animation: corgi-bob 1.2s ease-in-out infinite; transform-origin: center; }
        @keyframes corgi-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
      `}</style>
    </div>
  );
}

// ============= Phase 4 — Mission list =============
function MissionList({ missions, onComplete, onSkip }) {
  const active = missions.slots.filter((s) => !s.completed && !s.skipped);
  const completed = missions.slots.filter((s) => s.completed);
  return (
    <div className="neo-card p-2.5 bg-[#FFFEF5]" data-testid="walk-missions">
      <p className="text-xs font-bold mb-1.5 flex items-center gap-1">
        <Sparkles className="w-3.5 h-3.5" /> Uppdrag under promenaden ({active.length} öppna · {completed.length} klara)
      </p>
      <div className="space-y-1.5">
        {active.map((s) => (
          <div
            key={s.slot_idx}
            className="neo-card p-2 flex items-center gap-2 bg-white"
            data-testid={`walk-mission-${s.slot_idx}`}
          >
            <span className="text-xl shrink-0">{s.mission.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold">{s.mission.text}</p>
              <p className="text-[10px] opacity-60">Vid {s.km_at} km · +{s.mission.points}p</p>
            </div>
            <button
              onClick={() => onComplete(s.slot_idx)}
              className="neo-btn p-1.5 bg-[#B9FBC0]"
              data-testid={`walk-mission-complete-${s.slot_idx}`}
              title="Klarade det!"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
            <button
              onClick={() => onSkip(s.slot_idx)}
              className="neo-btn p-1.5 bg-white"
              data-testid={`walk-mission-skip-${s.slot_idx}`}
              title="Hoppa över"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {completed.length > 0 && (
          <details className="text-[11px]">
            <summary className="cursor-pointer opacity-70">✓ {completed.length} klarade idag</summary>
            <div className="mt-1 space-y-0.5">
              {completed.map((s) => (
                <p key={s.slot_idx} className="text-[11px] line-through opacity-60">
                  {s.mission.emoji} {s.mission.text} · +{s.mission.points}p
                </p>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ============= Phase 5 — Dog house builder =============
function DogHouse({ data }) {
  const { parts, current_house_parts_built, next_part_in_m, next_part_progress_pct, houses_completed, lifetime_km } = data;
  const houseComplete = current_house_parts_built >= 10;
  return (
    <div className="neo-card p-3 bg-gradient-to-br from-[#FFF5E6] to-[#FFE8F5]" data-testid="walk-doghouse">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold flex items-center gap-1">
          <Home className="w-3.5 h-3.5" /> Corgins hundhus
          {houses_completed > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#B9FBC0] border border-black text-[10px]">
              🏆 {houses_completed} klara
            </span>
          )}
        </p>
        <span className="text-[10px] opacity-60">Livstid: {lifetime_km.toFixed(2)} km</span>
      </div>

      {/* Visual assembly — stacked cute house */}
      <HouseAssembly parts={parts} />

      {houseComplete ? (
        <p className="text-[11px] font-bold text-center mt-2 text-[#D06892]">
          🎉 Huset är KLART! Nästa hus börjar byggas på nästa km 🏗️
        </p>
      ) : (
        <>
          <div className="mt-2 h-2 rounded-full bg-black/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FFC8DD] to-[#C8B6FF]" style={{ width: `${next_part_progress_pct}%` }} />
          </div>
          <p className="text-[10px] text-center mt-1 opacity-70">
            Nästa del "<b>{parts[current_house_parts_built]?.label}</b>" om {next_part_in_m} m 🐾
          </p>
        </>
      )}
    </div>
  );
}

function HouseAssembly({ parts }) {
  // Compact visual: renders a 5x2 grid of part cells, filled when built
  return (
    <div className="relative h-24 w-full bg-[#E8F7FF] rounded-xl border-2 border-black overflow-hidden" data-testid="doghouse-assembly">
      {/* Ground */}
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-[#C8E6C9] to-transparent" />
      {/* Sun */}
      <div className="absolute top-1 right-2 w-5 h-5 rounded-full bg-[#FFE08A] opacity-80" />
      {/* House schematic */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-2 w-24 h-20">
        {/* foundation (0) */}
        <div className={`absolute bottom-0 left-0 right-0 h-2 border-2 border-black ${parts[0]?.built ? 'bg-[#8D6E63]' : 'bg-[#E5E7EB] opacity-40'}`} />
        {/* floor (1) */}
        <div className={`absolute bottom-2 left-1 right-1 h-1 border-2 border-black ${parts[1]?.built ? 'bg-[#D7CCC8]' : 'bg-[#E5E7EB] opacity-40'}`} />
        {/* walls (2,3,4) */}
        <div className={`absolute bottom-3 left-1 w-2 h-10 border-2 border-black ${parts[2]?.built ? 'bg-[#FFCDD2]' : 'bg-[#E5E7EB] opacity-40'}`} />
        <div className={`absolute bottom-3 right-1 w-2 h-10 border-2 border-black ${parts[3]?.built ? 'bg-[#FFCDD2]' : 'bg-[#E5E7EB] opacity-40'}`} />
        <div className={`absolute bottom-3 left-3 right-3 h-10 border-2 border-black ${parts[4]?.built ? 'bg-[#FFF0F5]' : 'bg-[#E5E7EB] opacity-40'}`} />
        {/* door (5) */}
        {parts[5]?.built && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-4 h-6 bg-[#8D6E63] border-2 border-black rounded-t-full" />
        )}
        {/* window (6) */}
        {parts[6]?.built && (
          <div className="absolute bottom-11 right-4 w-3 h-3 bg-[#A2D2FF] border-2 border-black" />
        )}
        {/* roof (7) */}
        <div
          className={`absolute border-2 border-black ${parts[7]?.built ? 'bg-[#F48FB1]' : 'bg-[#E5E7EB] opacity-40'}`}
          style={{
            bottom: '52px',
            left: '-4px',
            right: '-4px',
            height: '10px',
            clipPath: 'polygon(10% 100%, 50% 0%, 90% 100%)',
          }}
        />
        {/* chimney (8) */}
        {parts[8]?.built && (
          <div className="absolute left-[70%] w-2 h-4 bg-[#8D6E63] border-2 border-black" style={{ bottom: '58px' }} />
        )}
        {/* flag (9) */}
        {parts[9]?.built && (
          <>
            <div className="absolute left-1/2 w-[2px] h-4 bg-black" style={{ bottom: '66px' }} />
            <div className="absolute left-[calc(50%+2px)] w-3 h-2 bg-[#FFC8DD] border border-black" style={{ bottom: '68px' }} />
          </>
        )}
      </div>
      {/* Bottom row — parts checklist */}
      <div className="absolute top-1 left-1 flex gap-0.5">
        {parts.map((p) => (
          <span
            key={p.idx}
            title={`${p.label}${p.built ? ' ✓' : ' — låst'}`}
            className={`text-[11px] ${p.built ? '' : 'opacity-30 grayscale'}`}
            data-testid={`doghouse-part-${p.idx}`}
          >
            {p.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
